import { isDrivable, latLngToLocalMeters, roadWidthMeters } from '@roadworld/map-generator';
import type { ChunkPayload, OsmWay, RoadClass } from '@roadworld/shared';
import type { HeightfieldData, MeshGeometry } from './geometry.js';
import { sampleHeightfield } from './heightfield.js';
import {
  buildSmoothedCenterline,
  sampleCenterlineAt,
  trimmedCenterline,
  type CenterlineSample,
} from './roadCenterline.js';

/** Y lift used by `buildRoadMeshes` for drivable roads. Decor must match so it aligns. */
const ROAD_LIFT = 0.1;
/** Centerline sample spacing in meters. 3m keeps painted lines smooth on curves while
 *  keeping decor triangle counts in check — every marking, shoulder, and dash emits a
 *  cross-section at this spacing, so it's the dominant lever on decor geometry cost. */
const SAMPLE_SPACING = 3;
/** Extra Y lift for paint markings above the road surface (combined with material zOffset).
 *  Markings sample terrain at their own lateral position (like the road ribbon's edges), so
 *  this is a pure paint-on-asphalt offset — no longer compensating for cross-slope mismatch. */
const MARKING_LIFT = 0.025;
/** Paint stripe width (each side of the centerline → total stripe is 2× this). */
const STRIPE_HALF_WIDTH = 0.12;
/** Edge stripe inset from the road edge — keeps paint slightly off the pavement boundary. */
const EDGE_STRIPE_INSET = 0.25;
/** Dash on / off lengths for highway-style center lines (meters). */
const HIGHWAY_DASH_ON = 3;
const HIGHWAY_DASH_OFF = 6;
/** Dash on / off for residential center lines (shorter, tighter rhythm). */
const RESIDENTIAL_DASH_ON = 1.5;
const RESIDENTIAL_DASH_OFF = 1.5;
/** Lane-divider dash uses the same rhythm as the highway center. */
const LANE_DASH_ON = HIGHWAY_DASH_ON;
const LANE_DASH_OFF = HIGHWAY_DASH_OFF;
/** Shoulder strip width on each side, in meters. Used for both the grass-tinted variant
 *  (rural / mid-class roads) and the pavement-tinted variant (residential "sidewalk"). */
const SHOULDER_WIDTH = 1.2;
/** Spacing between bollards along an edge (meters). */
const BOLLARD_SPACING = 25;
/** Inset of bollards from the road edge (sits just outside the asphalt). */
const BOLLARD_INSET = 0.5;
/** Intersection-fillet disk geometry. */
const FILLET_SIDES = 16;
const FILLET_LIFT = 0.005; // sits a hair above the road ribbon to hide corner gaps
/** Minimum deflection angle for an intersection to qualify for a fillet patch. */
const FILLET_MIN_ANGLE_DEG = 15;
/** Extra buffer beyond the cross-road's half-width when trimming a way's edge decor at
 *  a junction node — keeps markings/shoulder/bollards clearly off the other road's pavement. */
const JUNCTION_SETBACK_BUFFER = 0.5;
/** Maximum fraction of a way's length that can be eaten by junction setbacks on EACH end.
 *  Caps trimming so very short ways still produce some decor. */
const JUNCTION_SETBACK_MAX_FRAC = 0.45;
/** Setback multiplier applied at junctions where any incident way (self or other) is a
 *  `*_link` slip lane. Link merges happen at shallow angles over many meters of overlap,
 *  so they need more aggressive trimming than a standard crossroad to look like the two
 *  roads are yielding to each other rather than fighting for the same pavement. Applied
 *  to markings + shoulder only — edge lines and bollards already use a stronger constant
 *  setback at every junction (see EDGE_BOLLARD_SETBACK_MULTIPLIER). */
const LINK_SETBACK_MULTIPLIER = 2.5;
/** Setback multiplier applied to edge lines + bollards at EVERY junction. These two
 *  decor types are visually heavy — a continuous painted line or a vertical post sitting
 *  right at the mouth of a side road reads as a "wall" blocking that side road, even
 *  when the geometry technically clears the cross-road's pavement. Pulling them back
 *  ~3× the cross-road's half-width leaves visible breathing room around every junction
 *  so the two roads visually yield to each other. Markings and shoulder don't need this
 *  — they don't block the eye in the same way. */
const EDGE_BOLLARD_SETBACK_MULTIPLIER = 3.0;
/** Max midpoint-to-midpoint distance for two service segments to be considered a
 *  parking-lot pair. Beyond this they're treated as unrelated. */
const PARKING_FILL_MAX_GAP = 30;
/** Y lift above terrain for parking-lot fill. Between grass (0) and road ribbon (0.1)
 *  so the fill sits clearly over the terrain but service ribbons still win. */
const PARKING_FILL_LIFT = 0.05;
/** Pad obstacle segments by this much during the overlap check, so a path or road
 *  running RIGHT BESIDE a service road still blocks the fill (rather than slipping
 *  past on a technicality). */
const PARKING_OBSTACLE_BUFFER = 0.5;

/** Marking color: 'white' or 'yellow'. Used to split markings into two material groups. */
export type MarkingPaint = 'white' | 'yellow';

/** Shoulder variant: grass-tinted (rural / mid-class), pavement-tinted (sidewalk-like for
 *  residential), or no shoulder at all. */
export type ShoulderStyle = 'grass' | 'pavement' | false;

/** Edge-decoration style for a single road class. */
export interface RoadStyle {
  /** Center line: dashed paint between opposing flows. null = no center line. */
  center: { paint: MarkingPaint; dashOn: number; dashOff: number } | null;
  /** Continuous edge stripes (1 per side) — null = no edge lines. */
  edgeLines: { paint: MarkingPaint } | null;
  /** Internal lane dividers if the road has 3+ lanes. */
  laneDividers: { paint: MarkingPaint; dashOn: number; dashOff: number } | null;
  /** Flat shoulder strip outside the road on both sides. Variant picks the material. */
  shoulder: ShoulderStyle;
  /** Vertical posts every BOLLARD_SPACING along each shoulder. */
  bollards: boolean;
}

/**
 * Classify a road's decoration style from its `highway` tag (and `lanes` count when present).
 *
 * Buckets:
 *   - Motorway/trunk + links: full markings + bollards (guardrails come from the bridge system).
 *   - Primary + link: full markings + grass shoulder + bollards.
 *   - Secondary/tertiary + links: dashed center + grass shoulder (no edge lines, those clipped).
 *   - Residential: short white center dashes + pavement "sidewalk" strip on both sides.
 *   - Service/living_street: nothing — bare road ribbon.
 *   - Track/unclassified: long white center dashes + grass shoulder.
 *   - Pedestrian (footway/path/cycleway/steps/...): nothing — smoothing only.
 */
export function roadStyle(roadClass: RoadClass, lanes?: number): RoadStyle | null {
  const wantsLaneDividers = (lanes ?? 2) >= 4;
  const laneDividers = wantsLaneDividers
    ? { paint: 'white' as const, dashOn: LANE_DASH_ON, dashOff: LANE_DASH_OFF }
    : null;

  switch (roadClass) {
    case 'motorway':
    case 'trunk':
    case 'motorway_link':
    case 'trunk_link':
      return {
        center: { paint: 'white', dashOn: HIGHWAY_DASH_ON, dashOff: HIGHWAY_DASH_OFF },
        edgeLines: { paint: 'white' },
        laneDividers,
        shoulder: false,
        bollards: true,
      };
    case 'primary':
    case 'primary_link':
      return {
        center: { paint: 'white', dashOn: HIGHWAY_DASH_ON, dashOff: HIGHWAY_DASH_OFF },
        edgeLines: { paint: 'white' },
        laneDividers,
        shoulder: 'grass',
        bollards: true,
      };
    case 'secondary':
    case 'secondary_link':
    case 'tertiary':
    case 'tertiary_link':
      // Solid edge lines clipped too aggressively on these mid-class roads where the
      // ribbon often runs close to the pavement edge — dropped entirely. Center dashes
      // are still drawn.
      return {
        center: { paint: 'white', dashOn: HIGHWAY_DASH_ON, dashOff: HIGHWAY_DASH_OFF },
        edgeLines: null,
        laneDividers,
        shoulder: 'grass',
        bollards: false,
      };
    case 'residential':
    case 'road':
      // Short white center dashes + a flat pavement-colored strip on each side that reads
      // as a sidewalk without any of the raised-curb z-fighting problems.
      return {
        center: { paint: 'white', dashOn: RESIDENTIAL_DASH_ON, dashOff: RESIDENTIAL_DASH_OFF },
        edgeLines: null,
        laneDividers: null,
        shoulder: 'pavement',
        bollards: false,
      };
    case 'service':
    case 'living_street':
      // Parking-lot aisles + tiny back alleys — short white center dashes (residential
      // rhythm) help read each aisle. No shoulder/edge/bollards. The areas BETWEEN
      // service roads in a parking lot are painted by `buildParkingFillMeshes` so the
      // ground reads as one paved surface; this dashes are then trimmed back at aisle
      // intersections by the standard junction setback logic.
      return {
        center: { paint: 'white', dashOn: RESIDENTIAL_DASH_ON, dashOff: RESIDENTIAL_DASH_OFF },
        edgeLines: null,
        laneDividers: null,
        shoulder: false,
        bollards: false,
      };
    case 'track':
    case 'unclassified':
      // Rural / minor roads — long highway-rhythm white dashes (look right at speed) plus
      // a soft grass shoulder.
      return {
        center: { paint: 'white', dashOn: HIGHWAY_DASH_ON, dashOff: HIGHWAY_DASH_OFF },
        edgeLines: null,
        laneDividers: null,
        shoulder: 'grass',
        bollards: false,
      };
    default:
      // Footway / path / cycleway / steps / pedestrian / platform / bridleway / corridor —
      // smoothing only, no painted markings or edge decor.
      return null;
  }
}

/** Returned by `buildRoadDecorMeshes`. Separated by material so each can use its own zOffset. */
export interface RoadDecorMeshes {
  markingsWhite: MeshGeometry;
  markingsYellow: MeshGeometry;
  /** Green-tinted shoulder (rural / mid-class roads). */
  shoulderGrass: MeshGeometry;
  /** Gray-tinted "sidewalk" strip (residential roads). */
  shoulderPavement: MeshGeometry;
  /** Pavement fill between clusters of service roads — used to turn a "parking lot
   *  encoded as multiple service ways" from a grass-strip mess into a single paved area.
   *  Same dark color as drivable roads, drawn under the ribbon so service dashes win. */
  parkingFill: MeshGeometry;
  /** World-frame placement transforms for each bollard. */
  bollards: BollardPlacement[];
  /** Disk patches that round off junction corners. */
  intersectionFillets: MeshGeometry;
}

export interface BollardPlacement {
  /** Local meters (chunk frame). */
  x: number;
  z: number;
  /** Y at the base of the bollard. */
  y: number;
  /** Heading in radians — bollards are radially symmetric so this is for future props. */
  yaw: number;
}

/**
 * One-pass builder: walks every drivable way once, looks up its style, and emits the
 * appropriate marking / shoulder / bollard / fillet geometry. Each output mesh is keyed
 * by material so the client can upload them with their own material+zOffset.
 *
 * Tunnels and bridges are skipped entirely — bridges already have dedicated guardrail
 * geometry, and tunnels render no surface.
 */
export function buildRoadDecorMeshes(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
): RoadDecorMeshes {
  const markingsWhite = newGeomBuf();
  const markingsYellow = newGeomBuf();
  const shoulderGrass = newGeomBuf();
  const shoulderPavement = newGeomBuf();
  const bollards: BollardPlacement[] = [];

  // Per-node list of incident drivable ways (with their half-widths) — used to compute
  // per-way junction setbacks so each road's edge decor ends short of the cross-road's
  // pavement rather than running across it.
  const nodeJunctions = buildNodeJunctions(payload);

  for (const way of payload.osm.ways) {
    if (way.tunnel || way.bridge) continue;
    if (!isDrivable(way.roadClass)) continue;
    const style = roadStyle(way.roadClass, way.lanes);
    if (!style) continue;

    const centerline = buildSmoothedCenterline(payload, way, heightfield, ROAD_LIFT, SAMPLE_SPACING);
    if (!centerline || centerline.length < 2) continue;
    const baseWidth = roadWidthMeters(way.roadClass);
    const halfWidth = baseWidth * 0.5;

    // Arc-length windows we're allowed to decorate. Two tiers per endpoint:
    //
    //   - markings/shoulder: pull back by `cross-road half-width + buffer` (plus a link
    //     multiplier when a *_link is involved). Just enough that the decor doesn't
    //     extend onto the intersecting road's pavement.
    //
    //   - edge lines/bollards: always pull back ~3× the cross-road's half-width. These
    //     two are visually heavy enough that even a perpendicular crossroad needs more
    //     room or they look like a wall blocking the side road. Larger setback also
    //     means the start of the wider road's edge line near a small side road sits
    //     well clear of the junction.
    //
    // Both windows are capped at JUNCTION_SETBACK_MAX_FRAC of the way's total length
    // per end so very short ways still get some decor.
    const total = centerline[centerline.length - 1]!.distance;
    const firstNode = way.nodes[0]!;
    const lastNode = way.nodes[way.nodes.length - 1]!;
    const rawStart = setbackAt(nodeJunctions, firstNode, way.id, way.roadClass);
    const rawEnd = setbackAt(nodeJunctions, lastNode, way.id, way.roadClass);
    const cap = total * JUNCTION_SETBACK_MAX_FRAC;
    const mStart = Math.min(rawStart.markings, cap);
    const mEnd = Math.min(rawEnd.markings, cap);
    const eStart = Math.min(rawStart.edgeBollards, cap);
    const eEnd = Math.min(rawEnd.edgeBollards, cap);
    const markingsStartDist = mStart;
    const markingsEndDist = total - mEnd;
    const edgeStartDist = eStart;
    const edgeEndDist = total - eEnd;
    if (markingsEndDist - markingsStartDist < 0.5) continue;

    // Pre-trim two centerlines: a narrow trim (for markings + shoulder) and a wider
    // trim (for solid edge lines that pull back further). Dashed builders walk arc
    // length directly so they take the [startDist, endDist] window as numbers.
    const trimmedMarkings = trimmedCenterline(centerline, markingsStartDist, markingsEndDist);
    if (trimmedMarkings.length < 2) continue;
    const trimmedEdges = trimmedCenterline(centerline, edgeStartDist, edgeEndDist);

    if (style.center) {
      appendDashedLine(
        markingByPaint(style.center.paint, markingsWhite, markingsYellow),
        centerline,
        heightfield,
        0,
        style.center.dashOn,
        style.center.dashOff,
        markingsStartDist,
        markingsEndDist,
      );
    }
    if (style.edgeLines && trimmedEdges.length >= 2) {
      const buf = markingByPaint(style.edgeLines.paint, markingsWhite, markingsYellow);
      appendContinuousLine(buf, trimmedEdges, heightfield, halfWidth - EDGE_STRIPE_INSET);
      appendContinuousLine(buf, trimmedEdges, heightfield, -(halfWidth - EDGE_STRIPE_INSET));
    }
    if (style.laneDividers && way.lanes && way.lanes >= 4) {
      const buf = markingByPaint(style.laneDividers.paint, markingsWhite, markingsYellow);
      // Evenly distribute (lanes - 1) divider lines; skip index 0 if there's already a center
      // line at the same position to avoid double-painting. Lane dividers use the
      // markings-tier setback (not the heavier edge-line one) so they don't gap out.
      const lanes = way.lanes;
      const laneSpacing = baseWidth / lanes;
      for (let i = 1; i < lanes; i++) {
        const offset = -halfWidth + i * laneSpacing;
        // Skip the exact centerline (already drawn) — a small epsilon catches odd-lane cases.
        if (Math.abs(offset) < 0.05 && style.center) continue;
        appendDashedLine(
          buf,
          centerline,
          heightfield,
          offset,
          style.laneDividers.dashOn,
          style.laneDividers.dashOff,
          markingsStartDist,
          markingsEndDist,
        );
      }
    }
    if (style.shoulder === 'grass') {
      appendShoulderStrip(shoulderGrass, trimmedMarkings, heightfield, halfWidth);
    } else if (style.shoulder === 'pavement') {
      appendShoulderStrip(shoulderPavement, trimmedMarkings, heightfield, halfWidth);
    }
    if (style.bollards) {
      appendBollardPlacements(bollards, centerline, heightfield, halfWidth, edgeStartDist, edgeEndDist);
    }
  }

  const intersectionFillets = buildIntersectionFilletMeshes(payload, heightfield);
  const parkingFill = buildParkingFillMeshes(payload, heightfield);

  return {
    markingsWhite: finalizeGeom(markingsWhite),
    markingsYellow: finalizeGeom(markingsYellow),
    shoulderGrass: finalizeGeom(shoulderGrass),
    shoulderPavement: finalizeGeom(shoulderPavement),
    parkingFill,
    bollards,
    intersectionFillets,
  };
}

// ----------------------------------------------------------------------------- geometry buffers

interface GeomBuf {
  positions: number[];
  indices: number[];
  normals: number[];
  uvs: number[];
}

function newGeomBuf(): GeomBuf {
  return { positions: [], indices: [], normals: [], uvs: [] };
}

function finalizeGeom(g: GeomBuf): MeshGeometry {
  return {
    positions: new Float32Array(g.positions),
    indices: new Uint32Array(g.indices),
    normals: new Float32Array(g.normals),
    uvs: new Float32Array(g.uvs),
  };
}

function markingByPaint(paint: MarkingPaint, white: GeomBuf, yellow: GeomBuf): GeomBuf {
  return paint === 'yellow' ? yellow : white;
}

// ----------------------------------------------------------------------------- markings

/**
 * Emit dashed paint stripes along the centerline (or at a constant perpendicular offset
 * for lane-divider variants). `lateralOffset` is meters from the centerline, positive →
 * left side. `dashOn` and `dashOff` are arc-length intervals in meters.
 *
 * `startDist` and `endDist` clamp the painted region — used so dashes don't bleed across
 * intersecting roads. When omitted, the full centerline is painted.
 */
function appendDashedLine(
  buf: GeomBuf,
  centerline: CenterlineSample[],
  heightfield: HeightfieldData,
  lateralOffset: number,
  dashOn: number,
  dashOff: number,
  startDist = 0,
  endDist = Number.POSITIVE_INFINITY,
): void {
  const total = centerline[centerline.length - 1]!.distance;
  const hi = Math.min(endDist, total);
  const lo = Math.max(0, startDist);
  if (hi - lo < dashOn * 0.5) return;
  const period = dashOn + dashOff;
  let d = lo;
  while (d < hi) {
    const dashStart = d;
    const dashEnd = Math.min(d + dashOn, hi);
    appendStripQuad(buf, centerline, heightfield, dashStart, dashEnd, lateralOffset, STRIPE_HALF_WIDTH);
    d += period;
  }
}

/**
 * Emit a continuous painted strip parallel to the centerline at `lateralOffset` meters
 * from it (positive = left). Used for solid edge lines.
 */
function appendContinuousLine(
  buf: GeomBuf,
  centerline: CenterlineSample[],
  heightfield: HeightfieldData,
  lateralOffset: number,
): void {
  const base = buf.positions.length / 3;
  for (const s of centerline) {
    const cx = s.x + s.perpX * lateralOffset;
    const cz = s.z + s.perpZ * lateralOffset;
    // Sample terrain at the stripe's ACTUAL lateral position (matching how the road ribbon
    // computes its edge heights in road.ts) so the paint banks with the road's cross-slope.
    // Using the centerline height here would bury the uphill-edge line under the asphalt.
    const y = sampleHeightfield(heightfield, cx, cz) + ROAD_LIFT + MARKING_LIFT;
    // Left edge of the stripe (toward road center side).
    const lX = cx + s.perpX * STRIPE_HALF_WIDTH;
    const lZ = cz + s.perpZ * STRIPE_HALF_WIDTH;
    const rX = cx - s.perpX * STRIPE_HALF_WIDTH;
    const rZ = cz - s.perpZ * STRIPE_HALF_WIDTH;
    buf.positions.push(lX, y, lZ);
    buf.normals.push(0, 1, 0);
    buf.uvs.push(0, s.distance);
    buf.positions.push(rX, y, rZ);
    buf.normals.push(0, 1, 0);
    buf.uvs.push(1, s.distance);
  }
  for (let i = 0; i < centerline.length - 1; i++) {
    const a = base + i * 2;
    const b = base + i * 2 + 1;
    const c = base + (i + 1) * 2;
    const d = base + (i + 1) * 2 + 1;
    buf.indices.push(a, c, b, b, c, d);
  }
}

/**
 * Emit a single dash quad bracketed by arc-length samples at `startDist` and `endDist`,
 * centered laterally at `lateralOffset` from the centerline, half-width `halfWidthMark`.
 */
function appendStripQuad(
  buf: GeomBuf,
  centerline: CenterlineSample[],
  heightfield: HeightfieldData,
  startDist: number,
  endDist: number,
  lateralOffset: number,
  halfWidthMark: number,
): void {
  if (endDist - startDist < 0.05) return;
  const s = sampleCenterlineAt(centerline, startDist);
  const e = sampleCenterlineAt(centerline, endDist);
  const base = buf.positions.length / 3;
  // Start cross-section. Sample terrain at the dash's actual lateral position (see
  // appendContinuousLine) so it banks with the road instead of using centerline height.
  const sCx = s.x + s.perpX * lateralOffset;
  const sCz = s.z + s.perpZ * lateralOffset;
  const sY = sampleHeightfield(heightfield, sCx, sCz) + ROAD_LIFT + MARKING_LIFT;
  buf.positions.push(sCx + s.perpX * halfWidthMark, sY, sCz + s.perpZ * halfWidthMark);
  buf.normals.push(0, 1, 0);
  buf.uvs.push(0, 0);
  buf.positions.push(sCx - s.perpX * halfWidthMark, sY, sCz - s.perpZ * halfWidthMark);
  buf.normals.push(0, 1, 0);
  buf.uvs.push(1, 0);
  // End cross-section.
  const eCx = e.x + e.perpX * lateralOffset;
  const eCz = e.z + e.perpZ * lateralOffset;
  const eY = sampleHeightfield(heightfield, eCx, eCz) + ROAD_LIFT + MARKING_LIFT;
  buf.positions.push(eCx + e.perpX * halfWidthMark, eY, eCz + e.perpZ * halfWidthMark);
  buf.normals.push(0, 1, 0);
  buf.uvs.push(0, 1);
  buf.positions.push(eCx - e.perpX * halfWidthMark, eY, eCz - e.perpZ * halfWidthMark);
  buf.normals.push(0, 1, 0);
  buf.uvs.push(1, 1);
  buf.indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
}

// ----------------------------------------------------------------------------- shoulder

/**
 * Two flat strips, one each side of the road, between halfWidth and halfWidth+SHOULDER_WIDTH.
 * Strip Y follows the terrain (not the road's lift) so it blends into the surrounding
 * vegetation rather than sitting up on the road ribbon.
 *
 * Geometry is identical for grass-tinted and pavement-tinted variants; the caller picks
 * the right buffer and the client picks the matching material.
 */
function appendShoulderStrip(
  buf: GeomBuf,
  centerline: CenterlineSample[],
  heightfield: HeightfieldData,
  halfWidth: number,
): void {
  for (const side of [1, -1] as const) {
    const base = buf.positions.length / 3;
    for (const s of centerline) {
      const innerX = s.x + s.perpX * halfWidth * side;
      const innerZ = s.z + s.perpZ * halfWidth * side;
      const outerX = s.x + s.perpX * (halfWidth + SHOULDER_WIDTH) * side;
      const outerZ = s.z + s.perpZ * (halfWidth + SHOULDER_WIDTH) * side;
      // Sit a hair under the road ribbon so the road's markings still win.
      const innerY = sampleHeightfield(heightfield, innerX, innerZ) + ROAD_LIFT - 0.01;
      const outerY = sampleHeightfield(heightfield, outerX, outerZ) + 0.01;
      buf.positions.push(innerX, innerY, innerZ);
      buf.normals.push(0, 1, 0);
      buf.uvs.push(0, s.distance);
      buf.positions.push(outerX, outerY, outerZ);
      buf.normals.push(0, 1, 0);
      buf.uvs.push(1, s.distance);
    }
    for (let i = 0; i < centerline.length - 1; i++) {
      const a = base + i * 2;
      const b = base + i * 2 + 1;
      const c = base + (i + 1) * 2;
      const d = base + (i + 1) * 2 + 1;
      // Winding flipped for the right side to keep the top face upward.
      if (side === 1) {
        buf.indices.push(a, c, b, b, c, d);
      } else {
        buf.indices.push(a, b, c, b, d, c);
      }
    }
  }
}

// ----------------------------------------------------------------------------- bollards

function appendBollardPlacements(
  out: BollardPlacement[],
  centerline: CenterlineSample[],
  heightfield: HeightfieldData,
  halfWidth: number,
  startDist: number,
  endDist: number,
): void {
  const range = endDist - startDist;
  if (range < BOLLARD_SPACING) return;
  for (const side of [1, -1] as const) {
    // Center the spacing inside [startDist, endDist] so the first and last bollards sit
    // symmetrically away from the junction setbacks.
    const count = Math.floor(range / BOLLARD_SPACING);
    const leftover = range - count * BOLLARD_SPACING;
    const offsetIntoRange = leftover * 0.5 + BOLLARD_SPACING * 0.5;
    for (let i = 0; i < count; i++) {
      const d = startDist + offsetIntoRange + i * BOLLARD_SPACING;
      if (d >= endDist) break;
      const s = sampleCenterlineAt(centerline, d);
      const off = (halfWidth + BOLLARD_INSET) * side;
      const x = s.x + s.perpX * off;
      const z = s.z + s.perpZ * off;
      const y = sampleHeightfield(heightfield, x, z);
      const yaw = Math.atan2(s.dirZ, s.dirX);
      out.push({ x, z, y, yaw });
    }
  }
}

// ----------------------------------------------------------------------------- parking-lot fill

/** A 2D segment in chunk-local meters. */
interface Seg2 {
  ax: number;
  az: number;
  bx: number;
  bz: number;
}

/**
 * Detect parking lots encoded as a network of close-by `service` / `living_street` roads
 * and paint the area between them with the road color, so the lot reads as one paved
 * surface instead of a stack of gray strips on grass.
 *
 * Algorithm: for every PAIR of service segments whose midpoints are within
 * `PARKING_FILL_MAX_GAP` meters, compute the convex hull of the 4 endpoints and emit it
 * as a flat polygon. Reject pairs whose hull is intersected by, or contains the midpoint
 * of, any other-class road segment, any path segment, or any building outline — those
 * are the cases where "something else is between the service roads" and the fill would
 * be wrong.
 *
 * Many adjacent pairs in the same lot produce overlapping hulls. That's fine: they share
 * material so the visual result is one solid paved area, and overdraw cost is trivial
 * for flat geometry.
 */
function buildParkingFillMeshes(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
): MeshGeometry {
  const buf = newGeomBuf();

  // Collect service segments (the candidates we want to pair) and obstacle segments
  // (every other-class drivable road + every path/footway etc — anything that, if
  // present between two service roads, should kill the fill there).
  const serviceSegs: Array<{ wayId: number; seg: Seg2 }> = [];
  const obstacleSegs: Seg2[] = [];

  for (const way of payload.osm.ways) {
    if (way.tunnel || way.bridge) continue;
    const isService = way.roadClass === 'service' || way.roadClass === 'living_street';
    const seglist: Seg2[] = [];
    let prev: { x: number; z: number } | null = null;
    for (const nodeId of way.nodes) {
      const node = payload.osm.nodes[nodeId];
      if (!node) {
        prev = null;
        continue;
      }
      const { x, z } = latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng);
      if (prev) seglist.push({ ax: prev.x, az: prev.z, bx: x, bz: z });
      prev = { x, z };
    }
    if (isService) {
      for (const s of seglist) serviceSegs.push({ wayId: way.id, seg: s });
    } else {
      for (const s of seglist) obstacleSegs.push(s);
    }
  }

  // Building outlines as obstacle segments — adding them this way lets us reuse the
  // same intersection / midpoint-in-hull check we use for roads/paths. We don't need
  // the full polygon test for buildings; one edge crossing the hull is enough to reject.
  for (const b of payload.osm.buildings) {
    const ring: Array<{ x: number; z: number }> = [];
    for (const nodeId of b.outer) {
      const node = payload.osm.nodes[nodeId];
      if (!node) continue;
      ring.push(latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng));
    }
    for (let i = 0; i < ring.length - 1; i++) {
      obstacleSegs.push({ ax: ring[i]!.x, az: ring[i]!.z, bx: ring[i + 1]!.x, bz: ring[i + 1]!.z });
    }
  }

  // For each pair of service segments check distance, then hull, then obstacles.
  for (let i = 0; i < serviceSegs.length; i++) {
    const A = serviceSegs[i]!;
    const ax = (A.seg.ax + A.seg.bx) * 0.5;
    const az = (A.seg.az + A.seg.bz) * 0.5;
    for (let j = i + 1; j < serviceSegs.length; j++) {
      const B = serviceSegs[j]!;
      // Skip pairs from the same way — they're colinear and the hull degenerates.
      if (B.wayId === A.wayId) continue;
      const bx = (B.seg.ax + B.seg.bx) * 0.5;
      const bz = (B.seg.az + B.seg.bz) * 0.5;
      const dx = bx - ax;
      const dz = bz - az;
      if (dx * dx + dz * dz > PARKING_FILL_MAX_GAP * PARKING_FILL_MAX_GAP) continue;

      const hull = convexHullOf4(
        A.seg.ax, A.seg.az,
        A.seg.bx, A.seg.bz,
        B.seg.ax, B.seg.az,
        B.seg.bx, B.seg.bz,
      );
      if (hull.length < 3) continue;

      // Reject if any obstacle segment crosses the hull or has its midpoint inside.
      if (hullIsBlocked(hull, obstacleSegs)) continue;

      // Emit as a triangle fan around vertex 0. Sample terrain Y per vertex so the
      // fill follows the ground.
      const base = buf.positions.length / 3;
      for (const p of hull) {
        const y = sampleHeightfield(heightfield, p.x, p.z) + PARKING_FILL_LIFT;
        buf.positions.push(p.x, y, p.z);
        buf.normals.push(0, 1, 0);
        buf.uvs.push(0, 0);
      }
      for (let k = 1; k < hull.length - 1; k++) {
        buf.indices.push(base, base + k, base + k + 1);
      }
    }
  }

  return finalizeGeom(buf);
}

/**
 * Convex hull of up to 4 points in XZ, returned in CCW order. Uses a simple gift-wrap
 * sized for 4 inputs — overkill for general hulls but trivially correct here. Drops
 * duplicates within 1 cm to avoid degenerate output.
 */
function convexHullOf4(
  x0: number, z0: number,
  x1: number, z1: number,
  x2: number, z2: number,
  x3: number, z3: number,
): Array<{ x: number; z: number }> {
  const pts: Array<{ x: number; z: number }> = [];
  const candidates = [
    { x: x0, z: z0 },
    { x: x1, z: z1 },
    { x: x2, z: z2 },
    { x: x3, z: z3 },
  ];
  for (const c of candidates) {
    let dup = false;
    for (const p of pts) {
      if (Math.abs(p.x - c.x) < 0.01 && Math.abs(p.z - c.z) < 0.01) {
        dup = true;
        break;
      }
    }
    if (!dup) pts.push(c);
  }
  if (pts.length < 3) return pts;

  // Gift-wrap. Start from the lowest-Z (then lowest-X) point.
  let start = 0;
  for (let i = 1; i < pts.length; i++) {
    if (pts[i]!.z < pts[start]!.z || (pts[i]!.z === pts[start]!.z && pts[i]!.x < pts[start]!.x)) {
      start = i;
    }
  }
  const hull: Array<{ x: number; z: number }> = [];
  let cur = start;
  do {
    hull.push(pts[cur]!);
    let next = (cur + 1) % pts.length;
    for (let i = 0; i < pts.length; i++) {
      if (i === cur) continue;
      const cross =
        (pts[next]!.x - pts[cur]!.x) * (pts[i]!.z - pts[cur]!.z) -
        (pts[next]!.z - pts[cur]!.z) * (pts[i]!.x - pts[cur]!.x);
      if (cross > 0) next = i;
    }
    cur = next;
  } while (cur !== start && hull.length < pts.length);
  return hull;
}

/**
 * True if any obstacle segment intersects an edge of the hull or has its midpoint
 * inside the hull. Both checks honor `PARKING_OBSTACLE_BUFFER` by inflating the
 * obstacle around its midpoint check.
 */
function hullIsBlocked(
  hull: Array<{ x: number; z: number }>,
  obstacles: Seg2[],
): boolean {
  for (const ob of obstacles) {
    // Edge intersection.
    for (let i = 0; i < hull.length; i++) {
      const a = hull[i]!;
      const b = hull[(i + 1) % hull.length]!;
      if (segmentsIntersect(a.x, a.z, b.x, b.z, ob.ax, ob.az, ob.bx, ob.bz)) return true;
    }
    // Midpoint-inside test (catches obstacles that lie wholly inside the hull).
    const mx = (ob.ax + ob.bx) * 0.5;
    const mz = (ob.az + ob.bz) * 0.5;
    if (pointInPolygon(mx, mz, hull)) return true;
    // Also test the endpoints — a short obstacle whose midpoint sits on an edge would
    // otherwise sneak past. The buffer is applied by jittering the endpoint inward
    // toward the segment's midpoint (cheap, doesn't need real polygon offsetting).
    if (
      pointInPolygonBuffered(ob.ax, ob.az, mx, mz, hull, PARKING_OBSTACLE_BUFFER) ||
      pointInPolygonBuffered(ob.bx, ob.bz, mx, mz, hull, PARKING_OBSTACLE_BUFFER)
    ) {
      return true;
    }
  }
  return false;
}

/** Standard 2-segment intersection in 2D (XZ plane). Inclusive of touch. */
function segmentsIntersect(
  ax: number, az: number, bx: number, bz: number,
  cx: number, cz: number, dx: number, dz: number,
): boolean {
  const o1 = orient(ax, az, bx, bz, cx, cz);
  const o2 = orient(ax, az, bx, bz, dx, dz);
  const o3 = orient(cx, cz, dx, dz, ax, az);
  const o4 = orient(cx, cz, dx, dz, bx, bz);
  if (((o1 > 0 && o2 < 0) || (o1 < 0 && o2 > 0)) && ((o3 > 0 && o4 < 0) || (o3 < 0 && o4 > 0))) {
    return true;
  }
  // Collinear-and-overlapping cases are rare for our inputs; treat as non-intersecting
  // to avoid false positives on shared endpoints from junctions.
  return false;
}

function orient(ax: number, az: number, bx: number, bz: number, cx: number, cz: number): number {
  return (bx - ax) * (cz - az) - (bz - az) * (cx - ax);
}

/** Ray-cast point-in-polygon. Polygon in CCW order; result independent of winding. */
function pointInPolygon(px: number, pz: number, poly: Array<{ x: number; z: number }>): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i]!.x;
    const zi = poly[i]!.z;
    const xj = poly[j]!.x;
    const zj = poly[j]!.z;
    const intersects = (zi > pz) !== (zj > pz) && px < ((xj - xi) * (pz - zi)) / (zj - zi || 1e-9) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Point-in-polygon with a soft inward buffer. The test point is nudged `buffer` meters
 * toward (refX, refZ) before the check, so a point sitting just outside an edge by
 * less than `buffer` still counts as inside. Cheap approximation of polygon offsetting.
 */
function pointInPolygonBuffered(
  px: number, pz: number,
  refX: number, refZ: number,
  poly: Array<{ x: number; z: number }>,
  buffer: number,
): boolean {
  const dx = refX - px;
  const dz = refZ - pz;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return pointInPolygon(px, pz, poly);
  const t = Math.min(1, buffer / len);
  return pointInPolygon(px + dx * t, pz + dz * t, poly);
}

// ----------------------------------------------------------------------------- junction setbacks

/** Entry in the per-node incident-way map. */
interface JunctionWay {
  wayId: number;
  way: OsmWay;
  halfWidth: number;
}

/**
 * For every drivable OSM node, list the drivable ways that touch it (with their
 * half-widths). Tunnels and bridges are excluded — bridges have their own decor pipeline,
 * tunnels have no surface. Used to compute junction setbacks AND fillet placement so
 * both stay consistent (a node we trim against is also a node we draw a fillet at).
 */
function buildNodeJunctions(payload: ChunkPayload): Map<number, JunctionWay[]> {
  const map = new Map<number, JunctionWay[]>();
  for (const way of payload.osm.ways) {
    if (way.tunnel || way.bridge) continue;
    if (!isDrivable(way.roadClass)) continue;
    const halfWidth = roadWidthMeters(way.roadClass) * 0.5;
    for (const nodeId of way.nodes) {
      let list = map.get(nodeId);
      if (!list) {
        list = [];
        map.set(nodeId, list);
      }
      list.push({ wayId: way.id, way, halfWidth });
    }
  }
  return map;
}

/** True for the slip-lane variants of major roads (motorway_link, primary_link, …). */
function isLinkClass(roadClass: RoadClass): boolean {
  return (
    roadClass === 'motorway_link' ||
    roadClass === 'trunk_link' ||
    roadClass === 'primary_link' ||
    roadClass === 'secondary_link' ||
    roadClass === 'tertiary_link'
  );
}

/**
 * Per-feature arc-length setbacks for `selfWayId` at `nodeId`.
 *
 *   - `markings`: distance the centerline dashes / lane dividers / shoulder pull back
 *     from the junction node. Equal to the largest other-road half-width plus a small
 *     buffer (multiplied by LINK_SETBACK_MULTIPLIER at link junctions).
 *   - `edgeBollards`: distance the solid edge stripe and the bollard posts pull back
 *     from the junction node. Always uses EDGE_BOLLARD_SETBACK_MULTIPLIER × base, since
 *     even a perpendicular crossroad needs visible breathing room — a continuous edge
 *     stripe running right up to the corner reads as a wall blocking the side road.
 *
 * Returns 0/0 for dead-end nodes (no other incident way).
 */
function setbackAt(
  junctions: Map<number, JunctionWay[]>,
  nodeId: number,
  selfWayId: number,
  selfClass: RoadClass,
): { markings: number; edgeBollards: number } {
  const list = junctions.get(nodeId);
  if (!list || list.length < 2) return { markings: 0, edgeBollards: 0 };
  let maxOtherHalfWidth = 0;
  let linkInvolved = isLinkClass(selfClass);
  for (const e of list) {
    if (e.wayId === selfWayId) continue;
    if (e.halfWidth > maxOtherHalfWidth) maxOtherHalfWidth = e.halfWidth;
    if (!linkInvolved && isLinkClass(e.way.roadClass)) linkInvolved = true;
  }
  if (maxOtherHalfWidth === 0) return { markings: 0, edgeBollards: 0 };
  const base = maxOtherHalfWidth + JUNCTION_SETBACK_BUFFER;
  const markingsMult = linkInvolved ? LINK_SETBACK_MULTIPLIER : 1;
  // Edge/bollard setback never drops below the markings setback — if the link bump
  // would already exceed the constant 3×, use that instead.
  const edgeBollardsMult = Math.max(EDGE_BOLLARD_SETBACK_MULTIPLIER, markingsMult);
  return {
    markings: base * markingsMult,
    edgeBollards: base * edgeBollardsMult,
  };
}

// ----------------------------------------------------------------------------- intersection fillets

/**
 * Round off junction corners by laying a small disk at every OSM node where two or more
 * drivable ways meet. The disk uses the road color (added in the client material) and
 * sits a hair above the road surface so it covers the small triangular gaps where the
 * straight ribbons of two roads meet at an angle — and also covers the gap created by
 * the per-way junction setback that pulls edge decor back from the node.
 *
 * Skips near-collinear junctions (< FILLET_MIN_ANGLE_DEG total deflection) — those don't
 * leave visible gaps and a disk would just add unnecessary geometry.
 */
function buildIntersectionFilletMeshes(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
): MeshGeometry {
  const buf = newGeomBuf();
  const nodeJunctions = buildNodeJunctions(payload);

  for (const [nodeId, list] of nodeJunctions) {
    if (list.length < 2) continue;
    const node = payload.osm.nodes[nodeId];
    if (!node) continue;
    const { x, z } = latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng);

    // Use the widest road's half-width as the fillet radius — covers the largest gap.
    let maxHalfWidth = 0;
    for (const e of list) {
      if (e.halfWidth > maxHalfWidth) maxHalfWidth = e.halfWidth;
    }
    if (maxHalfWidth <= 0) continue;

    // Optional: skip junctions that are basically straight (degree-2 collinear).
    // Compute the in/out tangents of each way at this node and check the smallest angle.
    if (list.length === 2 && junctionIsCollinear(payload, [list[0]!.way, list[1]!.way], nodeId)) {
      continue;
    }

    const y = sampleHeightfield(heightfield, x, z) + ROAD_LIFT + FILLET_LIFT;
    appendDisk(buf, x, y, z, maxHalfWidth, FILLET_SIDES);
  }

  return finalizeGeom(buf);
}

/**
 * Compare the in-tangent and out-tangent of a degree-2 junction. If they're nearly aligned
 * (deflection < FILLET_MIN_ANGLE_DEG), the junction is collinear and doesn't need a fillet.
 *
 * Note: only meaningful for ways meeting at an interior node. For waypoints at the start
 * or end of a way (which is the normal case for junctions), we treat the way's direction
 * as "into the node" using the second-to-last segment.
 */
function junctionIsCollinear(payload: ChunkPayload, ways: OsmWay[], nodeId: number): boolean {
  const tangents: Array<{ x: number; z: number }> = [];
  for (const way of ways) {
    const t = wayTangentAtNode(payload, way, nodeId);
    if (t) tangents.push(t);
  }
  if (tangents.length < 2) return false;
  // For two ways, the junction is "straight" if the two incoming tangents are nearly
  // opposite (i.e. their dot product is close to -1).
  const a = tangents[0]!;
  const b = tangents[1]!;
  const dot = a.x * b.x + a.z * b.z;
  const minDeflection = Math.cos((180 - FILLET_MIN_ANGLE_DEG) * Math.PI / 180);
  return dot < minDeflection; // very negative → near-collinear
}

/** Unit vector pointing INTO the node from the way's adjacent node. Returns null on miss. */
function wayTangentAtNode(
  payload: ChunkPayload,
  way: OsmWay,
  nodeId: number,
): { x: number; z: number } | null {
  const idx = way.nodes.indexOf(nodeId);
  if (idx < 0) return null;
  const otherIdx = idx === 0 ? 1 : idx - 1;
  const node = payload.osm.nodes[nodeId];
  const other = payload.osm.nodes[way.nodes[otherIdx]!];
  if (!node || !other) return null;
  const a = latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng);
  const b = latLngToLocalMeters(other.lat, other.lng, payload.origin.lat, payload.origin.lng);
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  const len = Math.hypot(dx, dz) || 1;
  return { x: dx / len, z: dz / len };
}

/** Append a horizontal disk (regular polygon) centered at (cx, y, cz) with radius `r`. */
function appendDisk(buf: GeomBuf, cx: number, y: number, cz: number, r: number, sides: number): void {
  const base = buf.positions.length / 3;
  // Center vertex.
  buf.positions.push(cx, y, cz);
  buf.normals.push(0, 1, 0);
  buf.uvs.push(0.5, 0.5);
  for (let i = 0; i < sides; i++) {
    const a = (i / sides) * Math.PI * 2;
    const px = cx + Math.cos(a) * r;
    const pz = cz + Math.sin(a) * r;
    buf.positions.push(px, y, pz);
    buf.normals.push(0, 1, 0);
    buf.uvs.push(0.5 + Math.cos(a) * 0.5, 0.5 + Math.sin(a) * 0.5);
  }
  for (let i = 0; i < sides; i++) {
    const next = (i + 1) % sides;
    buf.indices.push(base, base + 1 + i, base + 1 + next);
  }
}
